import { useEffect, useRef, useState } from 'react';
import { getUserIdentityId } from '../auth';

// Constants
const MAX_AUTH_RETRIES = 10; // Max 20 seconds waiting for auth (10 * 2s)
const PING_INTERVAL_MS = 5 * 60 * 1000; // Send ping every 5 minutes (before 10-minute timeout)

/**
 * Custom hook for WebSocket-based real-time runbook completion notifications.
 *
 * Connects to API Gateway WebSocket API with Okta authentication.
 * Automatically reconnects on disconnection.
 *
 * @returns {Object} { notification, connectionStatus }
 *   - notification: Latest runbook completion notification object
 *   - connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error' | 'disabled'
 */
export function useWebSocketNotifications() {
  const wsRef = useRef(null);
  const [notification, setNotification] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const authRetryAttemptsRef = useRef(0);

  const formatNotificationContext = (data) => {
    const parts = [];
    if (data?.baseName) {
      parts.push(`source: ${data.baseName}`);
    }
    if (data?.executionId) {
      parts.push(`exec: ${data.executionId.split(':').pop()}`);
    }
    return parts.length > 0 ? ` [${parts.join(' | ')}]` : '';
  };

  useEffect(() => {
    const connectWebSocket = async () => {
      try {
        // Get user identity ID from Okta token (sub claim)
        const userId = await getUserIdentityId();

        if (!userId) {
          // Check if we've exceeded max authentication retries
          if (authRetryAttemptsRef.current >= MAX_AUTH_RETRIES) {
            console.error('[WebSocket] Max authentication retries exceeded - user may not be authenticated');
            setConnectionStatus('error');
            return;
          }

          authRetryAttemptsRef.current += 1;
          console.log(`[WebSocket] No identity ID available yet (attempt ${authRetryAttemptsRef.current}/${MAX_AUTH_RETRIES}) - waiting for authentication...`);
          setConnectionStatus('connecting');

          // Retry after delay (authentication may still be in progress)
          const retryDelay = 2000;
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, retryDelay);
          return;
        }

        // Reset auth retry counter on successful authentication
        authRetryAttemptsRef.current = 0;

        // Get WebSocket endpoint from config
        const wsEndpoint = window.amplifyConfig?.websocketEndpoint;
        if (!wsEndpoint || wsEndpoint === 'WEBSOCKET_URL_PLACEHOLDER') {
          console.warn('[WebSocket] WebSocket endpoint not configured - real-time notifications disabled');
          console.warn('[WebSocket] To enable real-time updates, ensure the Step Functions stack is deployed with WebSocket enabled');
          setConnectionStatus('disabled');
          return;
        }

        // Construct WebSocket URL with userId as query parameter
        const wsUrl = `${wsEndpoint}?userId=${encodeURIComponent(userId)}`;

        console.log('[WebSocket] Connecting...', { userId });
        setConnectionStatus('connecting');

        // Create WebSocket connection
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('[WebSocket] Connected successfully');
          console.log('[WebSocket] Connection details:', {
            url: wsUrl,
            userId: userId,
            readyState: ws.readyState
          });
          setConnectionStatus('connected');
          reconnectAttemptsRef.current = 0; // Reset reconnect counter

          // Start ping interval to keep connection alive
          // API Gateway has 10-minute idle timeout, so send ping every 5 minutes
          pingIntervalRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              console.log('[WebSocket] Sending keep-alive ping');
              ws.send(JSON.stringify({ action: 'ping', timestamp: new Date().toISOString() }));
            }
          }, PING_INTERVAL_MS);
          console.log(`[WebSocket] Keep-alive ping enabled (every ${PING_INTERVAL_MS / 1000}s)`);
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('[WebSocket] Message received:', message);

            // Handle pong response (keep-alive acknowledgment)
            if (message.type === 'pong') {
              console.log('[WebSocket] Received pong - connection alive');
              return;
            }

            // Ignore per-scenario notifications (too noisy for 3-30+ scenarios)
            if (message.type === 'scenario-html-complete' || message.type === 'scenario-markdown-complete') {
              console.log(`[WebSocket] Ignoring ${message.type} - per-scenario notifications disabled`);
              return;
            }

            // Handle Stage 1-2 completion (HTML diagrams + Markdown runbooks)
            if (message.type === 'stage1-2-complete') {
              console.log('[WebSocket] Stage 1-2 complete notification:', message.data);
              setNotification({ ...message.data, notificationType: 'stage1-2' });

              // Show browser notification if permission granted
              if (Notification.permission === 'granted') {
                const htmlCount = message.data.htmlDiagramCount || 0;
                const mdCount = message.data.markdownCount || 0;
                new Notification('Processing Complete! 📄', {
                  body: `Generated ${htmlCount} HTML diagrams and ${mdCount} markdown files.`,
                  icon: '/favicon.ico',
                  tag: 'stage12-notification',
                  requireInteraction: false
                });
              }
            }
            // Handle Markdown Ready notification (Combined markdown available)
            if (message.type === 'markdown-ready' || message.data?.stage === 'markdown-combined') {
              console.log('[WebSocket] Markdown ready notification:', message.data);
              setNotification({ ...message.data, notificationType: 'markdown-ready' });

              // Show browser notification if permission granted
              if (Notification.permission === 'granted') {
                const runbookCount = message.data.runbookCount || message.data.runbook_count || 0;
                new Notification('Markdown Ready! 📝', {
                  body: `Combined markdown with ${runbookCount} scenarios is ready${formatNotificationContext(message.data)}. HTML generation in progress...`,
                  icon: '/favicon.ico',
                  tag: 'markdown-notification',
                  requireInteraction: false
                });
              }
            }
            // Handle Batch Progress notification (Stage 3 HTML generation progress)
            if (message.type === 'batch-progress') {
              console.log('[WebSocket] Batch progress notification:', message.data);
              setNotification({ ...message.data, notificationType: 'batch-progress' });

              // No browser notification for progress updates (too frequent)
              // Just update the UI state
            }
            // Handle WF Core HTML as soon as it is ready
            if (message.type === 'wfcore-html-ready') {
              console.log('[WebSocket] WF Core HTML ready notification:', message.data);
              setNotification({ ...message.data, notificationType: 'wfcore-html-ready' });

              // Don't show browser notification for WF Core HTML - it's an intermediate file
              // if (Notification.permission === 'granted') {
              //   const fileName = message.data?.htmlKey?.split('/').pop() || 'HTML runbook';
              //   new Notification('WF Core HTML Ready! 📊', {
              //     body: `${fileName} is ready${formatNotificationContext(message.data)}.`,
              //     icon: '/favicon.ico',
              //     tag: 'wfcore-html-notification',
              //     requireInteraction: false
              //   });
              // }
            }
            // Handle WF Core Markdown as soon as it is ready
            if (message.type === 'wfcore-markdown-ready') {
              console.log('[WebSocket] WF Core Markdown ready notification:', message.data);
              setNotification({ ...message.data, notificationType: 'wfcore-markdown-ready' });

              if (Notification.permission === 'granted') {
                const markdownCount = message.data?.markdownKeys?.length || 0;
                new Notification('WF Core Markdown Ready! 📝', {
                  body: `${markdownCount} markdown file${markdownCount === 1 ? '' : 's'} ready${formatNotificationContext(message.data)}.`,
                  icon: '/favicon.ico',
                  tag: 'wfcore-markdown-notification',
                  requireInteraction: false
                });
              }
            }
            // Handle WF Core completion (WF Core HTML + Markdown runbooks)
            if (message.type === 'wfcore-complete') {
              console.log('[WebSocket] WF Core complete notification:', message.data);
              setNotification({ ...message.data, notificationType: 'wfcore-complete' });

              // Show browser notification if permission granted
              if (Notification.permission === 'granted') {
                new Notification('WF Core Runbooks Ready! 📊', {
                  body: `Workfront Kickstart HTML and Markdown runbooks generated${formatNotificationContext(message.data)}.`,
                  icon: '/favicon.ico',
                  tag: 'wfcore-notification',
                  requireInteraction: false
                });
              }
            }
            // Handle WF Core Standalone Final HTML completion
            if (message.type === 'wfcore-final-complete') {
              console.log('[WebSocket] WF Core standalone final HTML complete:', message.data);
              setNotification({ ...message.data, notificationType: 'wfcore-final' });

              // Show browser notification if permission granted
              if (Notification.permission === 'granted') {
                const fileName = message.data?.htmlKey?.split('/').pop() || 'Final HTML runbook';
                new Notification('WF Core Final Runbook Ready! 📑', {
                  body: `${fileName} is ready for download${formatNotificationContext(message.data)}.`,
                  icon: '/favicon.ico',
                  tag: 'wfcore-final-notification',
                  requireInteraction: false
                });
              }
            }
            // Handle Stage 3 completion (Final HTML runbook)
            if (message.type === 'runbook-complete' || message.data?.stage === 'final-html-complete') {
              console.log('[WebSocket] Runbook complete notification:', message.data);
              setNotification({ ...message.data, notificationType: 'final' });

              // Show browser notification if permission granted
              if (Notification.permission === 'granted') {
                const succeeded = message.data?.status !== 'failed' && Boolean(message.data?.htmlKey);
                new Notification(succeeded ? 'Runbook Ready! 📑' : 'Runbook Generation Stopped ⚠️', {
                  body: succeeded
                    ? `Your runbook with ${message.data.runbookCount} scenarios is ready for download${formatNotificationContext(message.data)}.`
                    : `${message.data?.error || 'Final HTML generation completed without creating a downloadable file.'}${formatNotificationContext(message.data)}`,
                  icon: '/favicon.ico',
                  tag: 'runbook-notification',
                  requireInteraction: false
                });
              } else if (Notification.permission === 'default') {
                // Request permission for future notifications
                Notification.requestPermission().then(permission => {
                  console.log('[WebSocket] Notification permission:', permission);
                });
              }
            }
          } catch (err) {
            console.error('[WebSocket] Error parsing message:', err, event.data);
          }
        };

        ws.onerror = (error) => {
          console.error('[WebSocket] Error:', error);
          setConnectionStatus('error');
        };

        ws.onclose = (event) => {
          console.log('[WebSocket] Connection closed:', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
          setConnectionStatus('disconnected');
          wsRef.current = null;

          // Clear ping interval
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
            console.log('[WebSocket] Keep-alive ping disabled');
          }

          // Attempt reconnection with exponential backoff (unless intentionally closed)
          if (event.code !== 1000) {
            const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            reconnectAttemptsRef.current += 1;

            console.log(`[WebSocket] Reconnecting in ${backoffDelay}ms (attempt ${reconnectAttemptsRef.current})...`);

            reconnectTimeoutRef.current = setTimeout(() => {
              console.log('[WebSocket] Attempting reconnection...');
              connectWebSocket();
            }, backoffDelay);
          }
        };

        wsRef.current = ws;
      } catch (error) {
        console.error('[WebSocket] Error connecting:', error);
        setConnectionStatus('error');

        // Retry connection after delay
        const retryDelay = 5000;
        console.log(`[WebSocket] Retrying in ${retryDelay}ms...`);
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, retryDelay);
      }
    };

    // Initial connection
    connectWebSocket();

    // Cleanup on unmount
    return () => {
      console.log('[WebSocket] Cleaning up...');

      // Clear reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      // Close WebSocket connection
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('[WebSocket] Closing connection (component unmounting)');
        wsRef.current.close(1000, 'Component unmounting');
      }
    };
  }, []); // Empty dependency array - connect once on mount

  return { notification, connectionStatus };
}
